import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { setLoading } from 'Actions/loading';
import {
    Box,
    Dialog,
    DialogContent,
    DialogTitle,
    Button,
    IconButton,
    FormControlLabel,
    Checkbox,
    Typography,
    Alert,
} from '@mui/material';
import { Transition } from 'Shared/Transition';
import { CustomSnackbar } from 'Shared/CustomSnackbar';
import { useSnackbar } from 'notistack';
import { CloseIcon } from 'Shared/Icons';
import { getFlowCompanyAccess, updateFlowCompanyAccess } from 'Utils/api/flows';

export const CompanyAccessModal = ({ open, flowId, onClose, onSave }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const [companies, setCompanies] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Загрузка списка компаний при открытии модалки
    useEffect(() => {
        if (open && flowId) {
            loadCompanies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, flowId]);

    const loadCompanies = () => {
        setIsLoading(true);
        setLoadError(null);

        getFlowCompanyAccess(flowId)
            .then((res) => {
                setCompanies(res.data.data || []);
                setHasChanges(false);
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.message || 'Failed to load companies';
                setLoadError(errorMessage);
                enqueueSnackbar(errorMessage, {
                    action: CustomSnackbar,
                    variant: 'error'
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleCheckboxChange = (companyId) => {
        setCompanies((prevCompanies) =>
            prevCompanies.map((company) =>
                company.company_id === companyId
                    ? {
                        ...company,
                        status: company.status === 'active' ? 'disabled' : 'active'
                      }
                    : company
            )
        );
        setHasChanges(true);
    };

    const handleSave = () => {
        const companiesToUpdate = companies.map((company) => ({
            company_id: company.company_id,
            status: company.status,
        }));

        const hasActiveCompany = companiesToUpdate.some(c => c.status === 'active');

        handleClose();

        updateFlowCompanyAccess(flowId, companiesToUpdate)
            .then((res) => {
                enqueueSnackbar('Company access updated successfully', {
                    action: CustomSnackbar,
                    variant: 'success'
                });

                if (onSave) {
                    onSave(hasActiveCompany);
                }
            })
            .catch((err) => {
                const errorMessage = err.response?.data?.message || 'Failed to update company access';
                enqueueSnackbar(errorMessage, {
                    action: CustomSnackbar,
                    variant: 'error'
                });
            });
    };

    const handleClose = () => {
        setCompanies([]);
        setLoadError(null);
        setHasChanges(false);
        onClose();
    };

    return (
        <Dialog
            sx={{
                '& .MuiDialog-paper': {
                    minWidth: '500px',
                    maxWidth: '600px',
                    backgroundColor: '#FAFEFF',
                    border: '1px solid #FFFFFF',
                    borderRadius: '16px',
                    boxShadow: '1px 3px 15px rgba(0, 0, 0, 0.1)',
                },
            }}
            open={open}
            onClose={handleClose}
            aria-labelledby="company-access-dialog-title"
            TransitionComponent={Transition}
        >
            <Box
                sx={{
                    position: 'absolute',
                    right: 0,
                }}
            >
                <IconButton
                    size="large"
                    aria-label="close"
                    color="inherit"
                    onClick={handleClose}
                >
                    <CloseIcon fontSize="large" />
                </IconButton>
            </Box>

            <DialogTitle
                id="company-access-dialog-title"
                color="primary.blackText"
                sx={{
                    fontSize: '2rem',
                    textAlign: 'center',
                    paddingBottom: '10px',
                }}
            >
                Company Access Settings
            </DialogTitle>

            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '20px 30px 30px 30px',
                    minHeight: '200px',
                }}
            >
                {loadError ? (
                    <Alert severity="error" sx={{ marginBottom: '16px' }}>
                        {loadError}
                    </Alert>
                ) : null}

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ marginBottom: '16px' }}
                >
                    Select companies that should have access to this flow:
                </Typography>

                <Box
                    className="scroll-container"
                    sx={{
                        overflowY: 'auto',
                        maxHeight: '400px',
                        marginBottom: '20px',
                        paddingRight: '10px',
                    }}
                >
                    {isLoading ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', padding: '20px' }}>
                            Loading...
                        </Typography>
                    ) : companies.length === 0 && !loadError ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', padding: '20px' }}>
                            No companies available
                        </Typography>
                    ) : (
                        companies.map((company) => (
                            <FormControlLabel
                                key={company.company_id}
                                control={
                                    <Checkbox
                                        checked={company.status === 'active'}
                                        onChange={() => handleCheckboxChange(company.company_id)}
                                        color="primary"
                                    />
                                }
                                label={company.company_name}
                                sx={{
                                    display: 'flex',
                                    marginBottom: '8px',
                                    '& .MuiFormControlLabel-label': {
                                        fontSize: '1rem',
                                        color: '#3B465C',
                                    },
                                }}
                            />
                        ))
                    )}
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        gap: '12px',
                        justifyContent: 'flex-end',
                    }}
                >
                    <Button
                        onClick={handleClose}
                        color="inherit"
                        variant="outlined"
                        sx={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '4px',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={!hasChanges}
                        sx={{
                            padding: '10px 24px',
                            fontSize: '14px',
                            borderRadius: '4px',
                        }}
                    >
                        Save Changes
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
};
